/**
 * The support calls a workspace has booked, against
 * `GET /api/v1/support-history/`.
 *
 * The bookings themselves live in Cal — our booker is Cal's embed — and this is
 * the backend relaying them back, which is what the history under the Support
 * tab's form is drawn from. It replaces the sample the tab used to show while
 * nothing listed them.
 *
 * Three things this file decides, and all are worth knowing before changing it:
 *
 *   - **A `SCHEDULED` call whose slot has passed reads as `Completed`.** Cal
 *     leaves a booking scheduled after its time; nothing marks it done. Drawn
 *     literally, last month's call would keep offering Join meeting on a room
 *     nobody is in — and, worse, would go on telling the form a call is already
 *     booked, shutting the one control on the tab that reaches anybody. So the
 *     end time is compared against the clock. This is the one inference here,
 *     and it is about a time that has passed rather than about what happened at
 *     it: a call that has been is still not one anybody says was attended.
 *   - **A call still ahead that replaced an earlier one reads `Rescheduled`.**
 *     Cal books a move as a fresh booking that points back at the one it
 *     replaced, and both go out as `SCHEDULED`; that pointer is the only trace
 *     of the move in what we are sent, and it is the difference between the
 *     time somebody has in their diary and the time the call is now at.
 *   - **Modify does not link to `modify_url`.** Cal sends its own booking page,
 *     but moving a call is picking a time out of the same month our own booking
 *     screen already draws, so the row carries the `cal_uid` and that screen
 *     opens the booker on it. See `SupportHistory`.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { isUpcomingStatus } from "../types/support.types";
import type {
  SupportHistoryResponse,
  SupportMeeting,
  SupportMeetingResponse,
  SupportMeetingStatus,
} from "../types/support.types";

/**
 * Where a booking stands, from Cal's word for it, the time it was for, and
 * whether it replaced an earlier booking.
 *
 * Anything Cal cancels, rejects or declines is a call that will not happen, and
 * they all read as `Cancelled`: the row says the call is off, and which of the
 * three words the backend used for it is not the reader's business.
 *
 * Everything else is a call that was made, so it is `Scheduled` until its slot
 * has passed and `Completed` after — including a status this app has never
 * heard of, because a booking nobody understood is still a booking somebody
 * made.
 *
 * A call still ahead that carries a `rescheduled_from` reads `Rescheduled`
 * instead. Cal books a move as a new booking pointing back at the one it
 * replaced, and sends both on as `SCHEDULED`; the pointer is the only trace of
 * the move, and without it the row would say the same thing as one nobody has
 * touched. It is only ever said of a call still ahead: once the slot has
 * passed, that the call moved on its way there is history nobody is acting on,
 * and a call that was called off is off whatever times it was booked for.
 */
const toStatus = (
  raw: string | null | undefined,
  endsAt: string,
  now: number,
  rescheduledFrom?: string | null
): SupportMeetingStatus => {
  const status = (raw ?? "").toUpperCase();

  if (status === "CANCELLED" || status === "REJECTED" || status === "DECLINED") {
    return "Cancelled";
  }
  if (status === "COMPLETED") return "Completed";

  // An unreadable or missing end time leaves the call ahead rather than behind:
  // the row that is still ahead is the one with something to do on it, and
  // hiding Join over a date we could not parse is the worse mistake of the two.
  const ends = Date.parse(endsAt);
  if (Number.isFinite(ends) && ends <= now) return "Completed";

  return rescheduledFrom ? "Rescheduled" : "Scheduled";
};

/**
 * The two things a booking's notes hold, taken back apart.
 *
 * The exact inverse of the join in `BookCallPage`: the support form puts the
 * reason its dropdown offered above the paragraph somebody typed, separated by
 * a blank line, and sends the pair to Cal as one field. They come back that
 * way, and the history wants them as two — the reason titles the row, and the
 * paragraph is what is written under it.
 *
 * A note with nothing after the blank line was not written by that form. Cal
 * takes notes on a booking made through its own pages too, and one of those is
 * a paragraph rather than a subject, so it is kept whole and the row keeps its
 * plain title. The same where the first part runs to more than one line, which
 * a reason picked off a list never does.
 */
export const splitNotes = (
  raw: string | null | undefined
): { reason?: string; notes?: string } => {
  const written = (raw ?? "").trim();
  if (!written) return {};

  const [head, ...rest] = written.split(/\n[ \t]*\n/);
  const message = rest.join("\n\n").trim();

  if (!message || head.includes("\n")) return { notes: written };

  return { reason: head.trim(), notes: message };
};

/**
 * One row, as the history draws it.
 *
 * The room and the id to move it are carried only while the call is still
 * ahead. Cal keeps sending both on a booking that has been or been called off,
 * and a Join that opens an empty room — or a Modify that offers to move a call
 * that is over — is a button that lies about what it will do.
 *
 * `now` is passed in rather than read here so that what a row says does not
 * depend on which millisecond it was mapped in: one clock reading maps the
 * whole list, and the tests can hold it still.
 */
export const toSupportMeeting = (
  row: SupportMeetingResponse,
  now: number = Date.now()
): SupportMeeting => {
  const startsAt = row.scheduled_start ?? "";
  const endsAt = row.scheduled_end ?? startsAt;
  const status = toStatus(row.status, endsAt, now, row.rescheduled_from);
  const upcoming = isUpcomingStatus(status);
  // Dropped where nothing is left: the endpoint sends an empty string rather
  // than nothing at all where the booking was made without either half, and a
  // heading over a blank line is a row that looks half-drawn.
  const { reason, notes } = splitNotes(row.notes);
  const cancellationReason = (row.cancellation_reason ?? "").trim();

  return {
    id: row.cal_uid,
    status,
    startsAt,
    ...(row.scheduled_end ? { endsAt: row.scheduled_end } : {}),
    ...(upcoming && row.meeting_url ? { joinUrl: row.meeting_url } : {}),
    // The id, not `modify_url`: Modify opens our own booking screen, which
    // builds the booker itself. A row with no id has nothing to move.
    ...(upcoming && row.cal_uid ? { rescheduleUid: row.cal_uid } : {}),
    ...(status === "Cancelled" && cancellationReason
      ? { cancellationReason }
      : {}),
    // Both kept whatever the status. Unlike the room and the id to move it,
    // what a call was asked for is not an offer to do something — and that is
    // worth as much on a call that has been as on one still to come.
    ...(reason ? { reason } : {}),
    ...(notes ? { notes } : {}),
  };
};

/**
 * Every support call this workspace has booked, newest first.
 *
 * Sorted here rather than trusted from the response. The order is what the
 * screen means by the top row — the call somebody still has to turn up to is
 * the one they came to this tab about — and it is not worth leaving to whichever
 * way a relay happened to hand the list over.
 *
 * A row with no `cal_uid` is dropped. It has no identity to key a list by and
 * nothing to move, and a booking we cannot name is not one we can offer to
 * change.
 *
 * Failures throw. A history that could not be read must not be drawn as an
 * empty one: "you have never booked a call" and "we could not find out" are
 * different sentences, and the form above it enforces a rule off these rows.
 */
export async function fetchSupportHistory(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<SupportMeeting[]> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Reaching this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot load your support calls.");
  }

  const response = await api.get<SupportHistoryResponse | null>(
    API_ENDPOINTS.support.history,
    { token, signal }
  );

  const now = Date.now();

  return (response?.support_history ?? [])
    .filter((row) => !!row?.cal_uid)
    .map((row) => toSupportMeeting(row, now))
    .sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt));
}
